import { forwardRef, useState, useId, type TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'onBlur'> {
  label: string;
  onValueChange?: (value: string) => void;
  onBlur?: (value: string) => void;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ label, onValueChange, onBlur, value, className, disabled, rows = 3, ...props }, ref) => {
    const id = useId();
    const [localValue, setLocalValue] = useState<string>(value?.toString() || '');
    const [isFocused, setIsFocused] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      if (onValueChange) {
        onValueChange(newValue);
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(localValue);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      if (value !== undefined && value !== null) {
        setLocalValue(String(value));
      }
    };

    const displayValue = isFocused ? localValue : (value !== undefined && value !== null ? String(value) : '');

    return (
      <div className={cn('space-y-1.5', className)}>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>

        <textarea
          ref={ref}
          id={id}
          rows={rows}
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
          className={cn(
            // Base styles
            'block w-full rounded-lg px-3.5 py-3',
            'text-gray-900 text-sm',
            'placeholder:text-gray-400',
            'resize-none',
            // Border and background
            'bg-white border-2 border-gray-200',
            'hover:border-gray-300',
            'focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10',
            // Transitions
            'transition-all duration-150',
            // Disabled state
            'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200',
            // Focus outline removal
            'focus:outline-none'
          )}
          {...props}
        />
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';
